import React from 'react';

import {
  alpha,
  Box,
  makeStyles,
  Typography,
  useTheme,
} from '@material-ui/core';
import clsx from 'clsx';

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: spacing(0),
  },
  rootColumn: {
    flexDirection: 'column',
  },
  rootRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  label: {
    fontWeight: 500,
    flexShrink: 0,
  },
  valueWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    minWidth: 0,
    width: '100%',
  },
  value: {
    overflow: 'hidden',
    marginTop: 1,
    wordBreak: 'break-word',
    paddingRight: spacing(1),
  },
  valueFullWidth: {
    width: '100%',
    maxWidth: '100%',
  },
}));

export interface DetailItemProps {
  label: string;
  value: React.ReactNode;
  layout?: 'column' | 'row';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
  ellipsis?: boolean;
  labelColor?: string;
  valueColor?: string;
  isContactItem?: boolean;
  labelMinWidth?: number;
  className?: string;
  marginBottom?: number;
}

export default function DetailItem({
  label,
  value,
  layout = 'column',
  startIcon,
  endIcon,
  fullWidth = false,
  ellipsis = false,
  labelColor,
  valueColor,
  isContactItem = false,
  labelMinWidth,
  className,
  marginBottom,
}: DetailItemProps) {
  const classes = useStyles();
  const theme = useTheme();

  const getLabelColor = () => {
    if (labelColor) return labelColor;
    if (isContactItem) return theme.palette.text.primary;
    return alpha(theme.palette.primary.main, 0.7);
  };

  const getValueColor = () => {
    if (valueColor) return valueColor;
    if (isContactItem) return alpha(theme.palette.text.primary, 0.7);
    return theme.palette.text.primary;
  };

  const rootClassName =
    layout === 'column' ? classes.rootColumn : classes.rootRow;

  return (
    <Box
      className={`${classes.root} ${rootClassName} ${className || ''}`}
      style={{ marginBottom: marginBottom !== undefined ? marginBottom : 1 }}
    >
      <Typography
        variant="caption"
        className={classes.label}
        style={{
          color: getLabelColor(),
          marginBottom: layout === 'column' ? 4 : 0,
          marginRight: layout === 'row' ? 8 : 0,
          minWidth: labelMinWidth,
        }}
      >
        {label}:
      </Typography>

      <Box className={classes.valueWrapper}>
        {startIcon && (
          <Box display="flex" alignItems="center">
            {startIcon}
          </Box>
        )}

        <Typography
          variant="body2"
          component="span"
          noWrap={ellipsis && !fullWidth}
          className={clsx(classes.value, {
            [classes.valueFullWidth]: fullWidth,
          })}
          style={{
            color: getValueColor(),
          }}
        >
          {value || ' - '}
        </Typography>

        {endIcon && (
          <Box display="flex" alignItems="center">
            {endIcon}
          </Box>
        )}
      </Box>
    </Box>
  );
}
